import axios, { AxiosInstance } from 'axios';
import { UserProfile, UpdateProfileData, AuthUser } from '@/types/auth';
import API_CONFIG from '@/config/api.config';
import { 
  getAuthToken, 
  setAuthToken, 
  removeAuthToken, 
  setRefreshToken, 
  removeRefreshToken 
} from '@/utils/token';

// Define API response types
export interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: string;
}

// Create axios instance with base config
const api: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to construct authentication API URLs
const getAuthUrl = (endpoint: string): string => {
  // Ensure no double slashes in the URL
  const baseUrl = API_CONFIG.BASE_URL.endsWith('/') 
    ? API_CONFIG.BASE_URL.slice(0, -1) 
    : API_CONFIG.BASE_URL;
    
  // Remove leading slash from endpoint if present
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  
  // Remove /api from base URL if endpoint already has it
  const authPath = API_CONFIG.ENDPOINTS.AUTH.startsWith('/') 
    ? API_CONFIG.ENDPOINTS.AUTH.slice(1) 
    : API_CONFIG.ENDPOINTS.AUTH;
    
  return `${baseUrl}/${authPath}/${cleanEndpoint}`.replace(/([^:]\/)\/+/g, '$1');
};

// Authentication API interfaces
export interface LoginCredentials {
  email?: string;
  username?: string;
  password: string;
}

// Interface for the actual backend response
export interface BackendLoginResponse {
  access_token: string;
  expires_in: string;
  token_type: string;
  user: AuthUser;
}

// Interface for the frontend's expected response format
export interface LoginResponseData {
  accessToken: string;
  refreshToken?: string; // Optional since our backend doesn't use refresh tokens yet
  user: AuthUser;
  expiresIn?: string;
  tokenType?: string;
}

export interface LoginResponse extends ApiResponse<LoginResponseData> {}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  user?: AuthUser;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface RequestPasswordResetData {
  email: string;
}

export interface ResetPasswordData {
  token: string;
  newPassword: string;
}

export interface AdminResetPasswordData {
  userId: string;
}

interface AuthApi {
  login: (credentials: LoginCredentials) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshToken: (token: string) => Promise<RefreshTokenResponse>;
  getProfile: () => Promise<UserProfile>;
  changePassword: (data: ChangePasswordData) => Promise<{ message: string }>;
  updateProfile: (data: UpdateProfileData) => Promise<UserProfile>;
  requestPasswordReset: (email: string) => Promise<{ message: string }>;
  resetPassword: (token: string, newPassword: string) => Promise<{ message: string }>;
  adminResetUserPassword: (userId: string) => Promise<{ message: string }>;
}

// Implementation of the AuthApi interface
const authApi: AuthApi = {
  // Login user
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      // Prepare the request payload based on login method
      const payload = {
        ...(credentials.email && { email: credentials.email }),
        ...(credentials.username && { username: credentials.username }),
        password: credentials.password
      };

      console.log('Sending login request to:', getAuthUrl('/login'));
      console.log('Payload:', { ...payload, password: '***' });

      // Make the API request with proper typing for the backend response
      const response = await api.post<BackendLoginResponse>(
        getAuthUrl('/login'),
        payload
      );
      
      console.log('Login response:', response.data);
      
      const { access_token, expires_in, token_type, user } = response.data;
      
      if (!access_token) {
        throw new Error('No access token received');
      }
      
      // Transform the backend response to match the frontend's expected format
      const loginResponse: LoginResponse = {
        data: {
          accessToken: access_token,
          // Don't include refreshToken since our backend doesn't use it yet
          user: user,
          expiresIn: expires_in,
          tokenType: token_type
        },
        message: 'Login successful',
        status: 'success'
      };
      
      console.log('Login response transformed:', loginResponse);
      
      return loginResponse;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  // Logout user
  async logout(): Promise<void> {
    try {
      const token = getAuthToken();
      if (token) {
        await api.post(
          getAuthUrl('/logout'),
          {},
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local auth data
      removeAuthToken();
      removeRefreshToken();
      localStorage.removeItem('user');
    }
  },

  // Refresh access token
  async refreshToken(token: string): Promise<RefreshTokenResponse> {
    try {
      const response = await api.post<ApiResponse<RefreshTokenResponse>>(
        getAuthUrl('/refresh-token'), 
        { token: token }
      );

      const { accessToken, refreshToken: newRefreshToken, user } = response.data.data;
      
      // Update stored tokens
      if (accessToken) {
        setAuthToken(accessToken);
      }
      
      if (newRefreshToken) {
        setRefreshToken(newRefreshToken);
      }
      
      // If user data is included in the response, update it in localStorage
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      return { accessToken, refreshToken: newRefreshToken, user };
    } catch (error) {
      console.error('Refresh token error:', error);
      // Clear auth data on refresh token failure
      removeAuthToken();
      removeRefreshToken();
      localStorage.removeItem('user');
      throw error;
    }
  },

  // Get user profile
  async getProfile(): Promise<UserProfile> {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await api.get<ApiResponse<UserProfile>>(
        getAuthUrl('/me'),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  },

  // Change user password
  async changePassword(data: ChangePasswordData): Promise<{ message: string }> {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await api.post<ApiResponse<{ message: string }>>(
        getAuthUrl('/change-password'),
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  },

  // Update user profile
  async updateProfile(data: UpdateProfileData): Promise<UserProfile> {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Only allow fields accepted by backend
      const allowedFields = ['username', 'email', 'firstName', 'lastName', 'phone'];
      const filteredData = Object.fromEntries(
        Object.entries(data).filter(([key]) => allowedFields.includes(key))
      );

      const response = await api.patch<ApiResponse<UserProfile>>(
        getAuthUrl('/profile'),
        filteredData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Update stored user data
      if (response.data.data) {
        localStorage.setItem('user', JSON.stringify(response.data.data));
      }

      return response.data.data;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  },

  // Request password reset
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    try {
      const response = await api.post<ApiResponse<{ message: string }>>(
        getAuthUrl('/request-password-reset'),
        { email }
      );

      return response.data.data;
    } catch (error) {
      console.error('Request password reset error:', error);
      throw error;
    }
  },

  // Reset password with token
  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    try {
      const response = await api.post<ApiResponse<{ message: string }>>(
        getAuthUrl('/reset-password'),
        { token, newPassword }
      );

      return response.data.data;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  },

  // Admin reset user password
  async adminResetUserPassword(userId: string): Promise<{ message: string }> {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await api.post<ApiResponse<{ message: string }>>(
        getAuthUrl(`/admin/reset-password/${userId}`),
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      return response.data.data;
    } catch (error) {
      console.error('Admin reset user password error:', error);
      throw error;
    }
  }
};

export { authApi };

export default authApi;
